import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { funilApi } from '@/api/funil';

import styles from './Billing.module.css';

/**
 * Pouso do Stripe pós-pagamento: `/billing/success?session_id=cs_...`.
 *
 * O webhook pode chegar DEPOIS do redirect — por isso a tela não assume nada:
 * pergunta ao backend (`confirmar-checkout`) e, se a confirmação ainda não
 * caiu, insiste algumas vezes antes de admitir "em processamento". Nunca
 * mostra erro duro para pagamento que só está atrasado.
 */
const MAX_RETENTATIVAS = 5;
const INTERVALO_MS = 3000;

type Fase = 'sem_sessao' | 'confirmando' | 'confirmado' | 'processando';

export function BillingSuccess() {
  const { t } = useTranslation(['funil', 'common']);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [fase, setFase] = useState<Fase>(sessionId ? 'confirmando' : 'sem_sessao');

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    const sid = sessionId;
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function tentar(retentativa: number) {
      try {
        const res = await funilApi.confirmarCheckout({ session_id: sid });
        if (cancelado) return;
        if (res.pagamento_confirmado) {
          setFase('confirmado');
          return;
        }
      } catch {
        if (cancelado) return;
        // 409/422/502 ou rede: trata como "ainda não confirmado" e insiste —
        // o polling existe justamente para cobrir o webhook atrasado.
      }
      if (retentativa >= MAX_RETENTATIVAS) {
        setFase('processando');
        return;
      }
      timer = setTimeout(() => void tentar(retentativa + 1), INTERVALO_MS);
    }

    void tentar(0);

    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('funil:billing_success.titulo')}</h1>
          <p className={styles.subtitle}>{t('funil:billing_success.subtitulo')}</p>
        </div>

        {fase === 'sem_sessao' && (
          <div className={styles.card}>
            <div className={styles.errorAlert}>
              {t('funil:billing_success.sem_sessao')}
            </div>
            <Link to="/billing">{t('funil:billing_success.voltar_billing')}</Link>
          </div>
        )}

        {fase === 'confirmando' && (
          <div className={styles.loading}>
            {t('funil:billing_success.confirmando')}
          </div>
        )}

        {fase === 'confirmado' && (
          <div className={styles.card}>
            <div className={styles.successAlert}>
              {t('funil:billing_success.sucesso')}
            </div>
            <Link to="/">{t('funil:billing_success.ir_para_painel')}</Link>
          </div>
        )}

        {fase === 'processando' && (
          <div className={styles.pendingCard}>
            <h3 className={styles.pendingTitle}>
              {t('funil:billing_success.processando_titulo')}
            </h3>
            <p>{t('funil:billing_success.processando')}</p>
            <Link to="/billing">{t('funil:billing_success.voltar_billing')}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
